// Reference (Справочники) — TanStack Query hooks.
// Query keys включают tenantId (см. features/clients/hooks/useClients.ts) + фильтры.
// Мутации инвалидируют соответствующий список (directions / levels).

import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';

import {
  listOccurrences,
  type OccurrenceItem,
  type ListOccurrencesParams,
} from '@/features/schedule/api/schedule.api';
import { ApiError } from '@/lib/api';
import { useTenantId } from '@/lib/tenant-context';

import {
  deleteReference,
  restoreReference,
  type ReferenceEntity,
  archiveAgeGroup,
  archiveBranch,
  archiveCategory,
  archiveDirection,
  archiveEmployee,
  dismissEmployee,
  getOffboardingSummary,
  archiveHall,
  archiveLevel,
  createAgeGroup,
  createBranch,
  createCategory,
  createDirection,
  createEmployee,
  createHall,
  createLevel,
  listAgeGroups,
  listBranches,
  listCategories,
  setCityDefaultResponsible,
  listCities,
  listDirections,
  listEmployees,
  listHalls,
  listLevels,
  updateAgeGroup,
  updateBranch,
  updateCategory,
  updateDirection,
  updateEmployee,
  updateHall,
  updateLevel,
  type AgeGroup,
  type Branch,
  type Category,
  type City,
  type CreateAgeGroupBody,
  type CreateBranchBody,
  type CreateCategoryBody,
  type CreateDirectionBody,
  type CreateEmployeeBody,
  type DismissEmployeeBody,
  type DismissEmployeeResult,
  type OffboardingSummary,
  type CreateHallBody,
  type CreateLevelBody,
  type Direction,
  type Employee,
  type Hall,
  type Level,
  type ListEmployeesParams,
  type UpdateAgeGroupBody,
  type UpdateBranchBody,
  type UpdateCategoryBody,
  type UpdateDirectionBody,
  type UpdateEmployeeBody,
  type UpdateHallBody,
  type UpdateLevelBody,
  // Phase 5: rental conditions + CRM dictionaries.
  archiveRentalCondition,
  createRentalCondition,
  listRentalConditions,
  updateRentalCondition,
  archiveDealSource,
  archiveStageReason,
  createDealSource,
  createStageReason,
  listDealSources,
  listStageReasons,
  updateStageReason,
  updateDealSource,
  archiveDealStage,
  createDealStage,
  listDealStages,
  updateDealStage,
  archiveDiscount,
  createDiscount,
  listDiscounts,
  updateDiscount,
  type Discount,
  type CreateDiscountBody,
  type UpdateDiscountBody,
  listPaymentMethods,
  updatePaymentMethod,
  archiveMessageTemplate,
  createMessageTemplate,
  listMessageTemplates,
  updateMessageTemplate,
  type CreateMessageTemplateBody,
  type MessageTemplateItem,
  type UpdateMessageTemplateBody,
  type PaymentMethodItem,
  type UpdatePaymentMethodBody,
  type RentalCondition,
  type CreateRentalConditionBody,
  type UpdateRentalConditionBody,
  type DealSource,
  type StageReason,
  type CreateReasonBody,
  type CreateSourceBody,
  type UpdateReasonBody,
  type UpdateSourceBody,
  type DealStage,
  type CreateStageBody,
  type UpdateStageBody,
  // Фискальные справочники: юр. лица (налоговая ставка и система налогообложения
  // — поля юр. лица, не отдельные справочники).
  archiveLegalEntity,
  createLegalEntity,
  listLegalEntities,
  updateLegalEntity,
  type LegalEntity,
  type CreateLegalEntityBody,
  type UpdateLegalEntityBody,
  // Группы (группа = название + направление + педагог) + занятия.
  listGroups,
  createGroup,
  updateGroup,
  archiveGroup,
  createOccurrence,
  listTeachers,
  type ScheduleGroup,
  type CreateGroupBody,
  type UpdateGroupBody,
  type ListGroupsParams,
  type CreateOccurrenceBody,
  type Teacher,
  reorderDealStages,
  deleteDealStage,
} from '../api/reference.api';

export interface ReferenceFilters {
  includeArchived?: boolean;
  /** Филиал продажи — сужает список до общих + привязанных к нему записей. */
  branchId?: string;
}

export function referenceKeys(tenantId: string | null) {
  const root = ['reference', tenantId] as const;
  return {
    root,
    categories: (filters: ReferenceFilters) => [...root, 'categories', filters] as const,
    directions: (filters: ReferenceFilters) => [...root, 'directions', filters] as const,
    levels: (filters: ReferenceFilters) => [...root, 'levels', filters] as const,
    ageGroups: (filters: ReferenceFilters) => [...root, 'ageGroups', filters] as const,
    halls: (filters: ReferenceFilters) => [...root, 'halls', filters] as const,
    employees: (filters: ListEmployeesParams) => [...root, 'employees', filters] as const,
    branches: (filters: ReferenceFilters) => [...root, 'branches', filters] as const,
    cities: () => [...root, 'cities'] as const,
    rentalConditions: (filters: ReferenceFilters) =>
      [...root, 'rentalConditions', filters] as const,
    dealSources: (filters: ReferenceFilters) => [...root, 'dealSources', filters] as const,
    stageReasons: (filters: ReferenceFilters) => [...root, 'stageReasons', filters] as const,
    dealStages: (filters: ReferenceFilters) => [...root, 'dealStages', filters] as const,
    discounts: (filters: ReferenceFilters) => [...root, 'discounts', filters] as const,
    paymentMethods: (filters: ReferenceFilters) => [...root, 'paymentMethods', filters] as const,
    messageTemplates: (filters: ReferenceFilters & { branchId?: string }) =>
      [...root, 'messageTemplates', filters] as const,
    legalEntities: (filters: ReferenceFilters) => [...root, 'legalEntities', filters] as const,
    groups: (filters: ListGroupsParams) => [...root, 'groups', filters] as const,
    groupOccurrences: (groupId: string) => [...root, 'groupOccurrences', groupId] as const,
    occurrences: (from: string, to: string) => [...root, 'occurrences', from, to] as const,
    teachers: () => [...root, 'teachers'] as const,
  };
}

// --- Generic restore / hard-delete (reference-admin) ------------------------

export function useRestoreReference(): UseMutationResult<
  void,
  Error,
  { entity: ReferenceEntity; id: string }
> {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ entity, id }) => restoreReference(entity, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.root }),
  });
}

export function useDeleteReference(): UseMutationResult<
  void,
  Error,
  { entity: ReferenceEntity; id: string }
> {
  const tenantId = useTenantId();
  const qc = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ entity, id }) => deleteReference(entity, id),
    onSuccess: () => void qc.invalidateQueries({ queryKey: keys.root }),
  });
}

// --- Directions -------------------------------------------------------------

export function useDirections(filters: ReferenceFilters = {}): UseQueryResult<Direction[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);

  return useQuery({
    queryKey: keys.directions(filters),
    queryFn: async () => (await listDirections(filters)).items,
  });
}

export function useCreateDirection(): UseMutationResult<
  { direction: Direction },
  Error,
  CreateDirectionBody
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);

  return useMutation({
    mutationFn: (body: CreateDirectionBody) => createDirection(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateDirection(): UseMutationResult<
  { direction: Direction },
  Error,
  { id: string; body: UpdateDirectionBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, body }) => updateDirection(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useArchiveDirection(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);

  return useMutation({
    mutationFn: (id: string) => archiveDirection(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// --- Levels -----------------------------------------------------------------

export function useLevels(filters: ReferenceFilters = {}): UseQueryResult<Level[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);

  return useQuery({
    queryKey: keys.levels(filters),
    queryFn: async () => (await listLevels(filters)).items,
  });
}

export function useCreateLevel(): UseMutationResult<{ level: Level }, Error, CreateLevelBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);

  return useMutation({
    mutationFn: (body: CreateLevelBody) => createLevel(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateLevel(): UseMutationResult<
  { level: Level },
  Error,
  { id: string; body: UpdateLevelBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);

  return useMutation({
    mutationFn: ({ id, body }) => updateLevel(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useArchiveLevel(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);

  return useMutation({
    mutationFn: (id: string) => archiveLevel(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// --- Categories (Mobifitness «Направление») ---------------------------------

export function useCategories(filters: ReferenceFilters = {}): UseQueryResult<Category[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.categories(filters),
    queryFn: async () => (await listCategories(filters)).items,
  });
}

export function useCreateCategory(): UseMutationResult<
  { category: Category },
  Error,
  CreateCategoryBody
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (body: CreateCategoryBody) => createCategory(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateCategory(): UseMutationResult<
  { category: Category },
  Error,
  { id: string; body: UpdateCategoryBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, body }) => updateCategory(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useArchiveCategory(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (id: string) => archiveCategory(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// --- Age groups (Mobifitness «Возраст») -------------------------------------

export function useAgeGroups(filters: ReferenceFilters = {}): UseQueryResult<AgeGroup[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.ageGroups(filters),
    queryFn: async () => (await listAgeGroups(filters)).items,
  });
}

export function useCreateAgeGroup(): UseMutationResult<
  { ageGroup: AgeGroup },
  Error,
  CreateAgeGroupBody
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (body: CreateAgeGroupBody) => createAgeGroup(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateAgeGroup(): UseMutationResult<
  { ageGroup: AgeGroup },
  Error,
  { id: string; body: UpdateAgeGroupBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, body }) => updateAgeGroup(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useArchiveAgeGroup(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (id: string) => archiveAgeGroup(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// --- Halls (Mobifitness «Залы») ---------------------------------------------

export function useHalls(filters: ReferenceFilters = {}): UseQueryResult<Hall[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.halls(filters),
    queryFn: async () => (await listHalls(filters)).items,
  });
}

export function useCreateHall(): UseMutationResult<{ hall: Hall }, Error, CreateHallBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (body: CreateHallBody) => createHall(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateHall(): UseMutationResult<
  { hall: Hall },
  Error,
  { id: string; body: UpdateHallBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, body }) => updateHall(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useArchiveHall(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (id: string) => archiveHall(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// --- Employees (Mobifitness «Педагоги/Сотрудники») --------------------------

/**
 * Список сотрудников. `enabled` (план 62 П2) — чтобы вызывающий мог отложить
 * запрос: GET /employees закрыт правом employee:manage, и подтягивать список
 * ради одной карточки до клика значит ловить лишние 403 у ролей без права.
 */
export function useEmployees(
  filters: ListEmployeesParams = {},
  opts: { enabled?: boolean } = {},
): UseQueryResult<Employee[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.employees(filters),
    queryFn: async () => (await listEmployees(filters)).items,
    ...(opts.enabled === undefined ? {} : { enabled: opts.enabled }),
  });
}

export function useCreateEmployee(): UseMutationResult<
  { employee: Employee },
  Error,
  CreateEmployeeBody
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (body: CreateEmployeeBody) => createEmployee(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateEmployee(): UseMutationResult<
  { employee: Employee },
  Error,
  { id: string; body: UpdateEmployeeBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, body }) => updateEmployee(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/** Сводка хвостов для экрана увольнения; грузится только когда id задан. */
export function useOffboardingSummary(
  employeeId: string | null,
): UseQueryResult<OffboardingSummary, Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: [...keys.root, 'offboarding', employeeId],
    queryFn: () => getOffboardingSummary(employeeId as string),
    enabled: Boolean(employeeId),
  });
}

export function useDismissEmployee(): UseMutationResult<
  DismissEmployeeResult,
  Error,
  { id: string; body: DismissEmployeeBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, body }) => dismissEmployee(id, body),
    onSuccess: () => {
      // Увольнение трогает расписание, клиентов и задачи — обновляем всё,
      // а не только справочник сотрудников.
      void queryClient.invalidateQueries({ queryKey: keys.root });
      void queryClient.invalidateQueries({ queryKey: ['clients'] });
      void queryClient.invalidateQueries({ queryKey: ['schedule'] });
    },
  });
}

export function useArchiveEmployee(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (id: string) => archiveEmployee(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// --- Branches / Cities (Mobifitness «Локация») ------------------------------

export function useBranchCards(filters: ReferenceFilters = {}): UseQueryResult<Branch[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.branches(filters),
    queryFn: async () => (await listBranches(filters)).items,
  });
}

export function useCities(): UseQueryResult<City[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.cities(),
    queryFn: async () => (await listCities()).items,
    staleTime: 5 * 60_000,
  });
}

export function useCreateBranch(): UseMutationResult<{ branch: Branch }, Error, CreateBranchBody> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (body: CreateBranchBody) => createBranch(body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useUpdateBranch(): UseMutationResult<
  { branch: Branch },
  Error,
  { id: string; body: UpdateBranchBody }
> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: ({ id, body }) => updateBranch(id, body),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

export function useArchiveBranch(): UseMutationResult<void, Error, string> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: (id: string) => archiveBranch(id),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

/** Мутация, инвалидирующая весь reference-кэш по успеху. */
function useRefMutation<TArgs, TRes>(
  fn: (args: TArgs) => Promise<TRes>,
): UseMutationResult<TRes, Error, TArgs> {
  const tenantId = useTenantId();
  const queryClient = useQueryClient();
  const keys = referenceKeys(tenantId);
  return useMutation({
    mutationFn: fn,
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: keys.root });
    },
  });
}

// --- Phase 5: rental conditions + CRM dictionaries --------------------------

export function useRentalConditions(
  filters: ReferenceFilters = {},
): UseQueryResult<RentalCondition[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.rentalConditions(filters),
    queryFn: async () => (await listRentalConditions(filters)).items,
  });
}
export const useCreateRentalCondition = () =>
  useRefMutation((b: CreateRentalConditionBody) => createRentalCondition(b));
export const useUpdateRentalCondition = () =>
  useRefMutation((a: { id: string; body: UpdateRentalConditionBody }) =>
    updateRentalCondition(a.id, a.body),
  );
export const useArchiveRentalCondition = () =>
  useRefMutation((id: string) => archiveRentalCondition(id));

export function useDealSources(
  filters: ReferenceFilters = {},
): UseQueryResult<DealSource[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.dealSources(filters),
    queryFn: async () => (await listDealSources(filters)).items,
  });
}
export const useCreateDealSource = () =>
  useRefMutation((b: CreateSourceBody) => createDealSource(b));
export const useUpdateDealSource = () =>
  useRefMutation((a: { id: string; body: UpdateSourceBody }) => updateDealSource(a.id, a.body));
export const useArchiveDealSource = () => useRefMutation((id: string) => archiveDealSource(id));

export function useStageReasons(
  filters: ReferenceFilters = {},
): UseQueryResult<StageReason[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.stageReasons(filters),
    queryFn: async () => (await listStageReasons(filters)).items,
  });
}
export const useCreateStageReason = () =>
  useRefMutation((b: CreateReasonBody) => createStageReason(b));
export const useUpdateStageReason = () =>
  useRefMutation((a: { id: string; body: UpdateReasonBody }) => updateStageReason(a.id, a.body));
export const useArchiveStageReason = () => useRefMutation((id: string) => archiveStageReason(id));

export function useDealStages(filters: ReferenceFilters = {}): UseQueryResult<DealStage[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.dealStages(filters),
    queryFn: async () => (await listDealStages(filters)).items,
  });
}
export const useCreateDealStage = () => useRefMutation((b: CreateStageBody) => createDealStage(b));
export const useUpdateDealStage = () =>
  useRefMutation((a: { id: string; body: UpdateStageBody }) => updateDealStage(a.id, a.body));
export const useArchiveDealStage = () => useRefMutation((id: string) => archiveDealStage(id));
/** Порядок стадий целиком (план 77). */
export const useReorderDealStages = () => useRefMutation((ids: string[]) => reorderDealStages(ids));
/** Жёсткое удаление стадии с переносом сделок (план 77). */
export const useDeleteDealStage = () =>
  useRefMutation((v: { id: string; moveTo?: string }) => deleteDealStage(v.id, v.moveTo));

// --- Скидки (справочник) ----------------------------------------------------
export function useDiscounts(filters: ReferenceFilters = {}): UseQueryResult<Discount[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.discounts(filters),
    queryFn: async () => (await listDiscounts(filters)).items,
  });
}
export const useCreateDiscount = () => useRefMutation((b: CreateDiscountBody) => createDiscount(b));
export const useUpdateDiscount = () =>
  useRefMutation((a: { id: string; body: UpdateDiscountBody }) => updateDiscount(a.id, a.body));
export const useArchiveDiscount = () => useRefMutation((id: string) => archiveDiscount(id));

// --- Способы оплаты (справочник) --------------------------------------------
export function usePaymentMethods(
  filters: ReferenceFilters = {},
): UseQueryResult<PaymentMethodItem[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.paymentMethods(filters),
    queryFn: async () => (await listPaymentMethods(filters)).items,
  });
}
export const useUpdatePaymentMethod = () =>
  useRefMutation((a: { id: string; body: UpdatePaymentMethodBody }) =>
    updatePaymentMethod(a.id, a.body),
  );

// --- Шаблоны сообщений мессенджера (М6 батча №20) ---------------------------
// branchId — фильтр композера чата: шаблоны филиала + общешкольные (без локаций).
export function useMessageTemplates(
  filters: ReferenceFilters & { branchId?: string } = {},
  /** false — не ходить в сеть (поповер шаблонов закрыт). */
  options: { enabled?: boolean } = {},
): UseQueryResult<MessageTemplateItem[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.messageTemplates(filters),
    queryFn: async () => (await listMessageTemplates(filters)).items,
    enabled: options.enabled ?? true,
  });
}
export const useCreateMessageTemplate = () =>
  useRefMutation((b: CreateMessageTemplateBody) => createMessageTemplate(b));
export const useUpdateMessageTemplate = () =>
  useRefMutation((a: { id: string; body: UpdateMessageTemplateBody }) =>
    updateMessageTemplate(a.id, a.body),
  );
export const useArchiveMessageTemplate = () =>
  useRefMutation((id: string) => archiveMessageTemplate(id));

// --- Фискальные справочники (юр. лица) ---------------------------------------
// Налоговая ставка и система налогообложения — поля юр. лица, а не отдельные
// справочники (см. LegalEntity в reference.api.ts).

export function useLegalEntities(
  filters: ReferenceFilters = {},
): UseQueryResult<LegalEntity[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.legalEntities(filters),
    queryFn: async () => (await listLegalEntities(filters)).items,
  });
}
export const useCreateLegalEntity = () =>
  useRefMutation((b: CreateLegalEntityBody) => createLegalEntity(b));
export const useUpdateLegalEntity = () =>
  useRefMutation((a: { id: string; body: UpdateLegalEntityBody }) =>
    updateLegalEntity(a.id, a.body),
  );
export const useArchiveLegalEntity = () => useRefMutation((id: string) => archiveLegalEntity(id));

// --- Группы (группа = название + направление + педагог) ----------------------

export function useGroups(filters: ListGroupsParams = {}): UseQueryResult<ScheduleGroup[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.groups(filters),
    queryFn: async () => (await listGroups(filters)).items,
  });
}

export const useCreateGroup = () => useRefMutation((b: CreateGroupBody) => createGroup(b));
export const useUpdateGroup = () =>
  useRefMutation((a: { id: string; body: UpdateGroupBody }) => updateGroup(a.id, a.body));
export const useArchiveGroup = () => useRefMutation((id: string) => archiveGroup(id));

// --- Занятия (занятие = группа + дата + время) -------------------------------

/**
 * Список занятий в окне [from, to] (ISO). Используется вкладкой «Занятия».
 */
export function useOccurrences(
  fromIso: string,
  toIso: string,
): UseQueryResult<OccurrenceItem[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    queryKey: keys.occurrences(fromIso, toIso),
    queryFn: async () => {
      const params: ListOccurrencesParams = { from: fromIso, to: toIso, limit: 200 };
      return (await listOccurrences(params)).items;
    },
  });
}

export const useCreateOccurrence = () =>
  useRefMutation((b: CreateOccurrenceBody) => createOccurrence(b));

/** Одно занятие в батче: тело + человекочитаемая метка (дата) для отчёта об ошибке. */
export interface OccurrenceBatchItem {
  body: CreateOccurrenceBody;
  label: string;
}

/** Итог батч-создания занятий: сколько создано и какие даты упали (с причиной). */
export interface OccurrenceBatchResult {
  created: number;
  failures: Array<{ label: string; message: string }>;
}

/**
 * Батч-создание занятий (мультивыбор дней в «Справочниках»). Создаёт каждое
 * занятие независимо (Promise.allSettled) — частичный успех не теряется:
 * конфликт по одному дню (напр. зал занят) не отменяет остальные. Возвращает
 * сводку {created, failures}. Инвалидация — один раз через useRefMutation.
 */
export const useCreateOccurrencesBatch = () =>
  useRefMutation(async (items: OccurrenceBatchItem[]): Promise<OccurrenceBatchResult> => {
    const settled = await Promise.allSettled(items.map((it) => createOccurrence(it.body)));
    const failures: OccurrenceBatchResult['failures'] = [];
    let created = 0;
    settled.forEach((res, i) => {
      if (res.status === 'fulfilled') {
        created += 1;
        return;
      }
      const err: unknown = res.reason;
      const message =
        err instanceof ApiError ? (err.problem.detail ?? 'ошибка создания') : 'сетевая ошибка';
      failures.push({ label: items[i]?.label ?? '—', message });
    });
    return { created, failures };
  });

/** Педагоги для дропдаунов — открытый lookup (без boss-права employee:manage). */
export function useTeachers(branchId?: string): UseQueryResult<Teacher[], Error> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  return useQuery({
    // branchId в ключе — иначе список одного филиала показался бы в форме
    // другого из кэша.
    queryKey: [...keys.teachers(), branchId ?? null],
    queryFn: async () => (await listTeachers(branchId)).items,
    staleTime: 5 * 60_000,
  });
}

/**
 * Ответственный по умолчанию для сделок города (план 104 Б7). Инвалидируем и
 * города, и филиалы: карточка филиала показывает настройку своего города.
 */
export function useSetCityDefaultResponsible(): UseMutationResult<
  { ok: boolean },
  Error,
  { cityId: string; employeeId: string | null }
> {
  const tenantId = useTenantId();
  const keys = referenceKeys(tenantId);
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ cityId, employeeId }) => setCityDefaultResponsible(cityId, employeeId),
    onSuccess: async () => {
      await qc.invalidateQueries({ queryKey: keys.cities() });
    },
  });
}
